import {
	AccountingUserStatusEnum,
	AccountingUserTypeEnum,
	BoostPackageNameEnum,
	CoinAddedByEnum,
	PromotionStatusEnum,
	CoinPackageNameEnum,
	CountryEnum,
	IncognitoPackageNameEnum,
	CreatorTypeEnum,
	CurrencyEnum,
	DataTablePageSizeEnum,
	DmStateEnum,
	MonthEnum,
	PaymentMethodEnum,
	PaymentMethodRequestStatusEnum,
	ProductsEnum,
	ReportReasonEnum,
	TransactionCategoryEnum,
	TransactionTypeEnum,
	UserRewardSourceEnum,
	ValueTypeEnum,
	VerificationStateEnum,
	YesOrNoEnum,
} from '@/enums';
import {
	AdminUserPermissionsEnum,
	GenderEnum,
	UserAccountStatusEnum,
	VerificationTypesEnum,
} from '@/lib/enum';
import type { ISelectOption } from '@/interfaces';
import type{ TNomenclature } from '@/types';
import { countries } from '@/config';
import { PROMOTION_PRODUCT_PACKAGES } from '@/constants';

type TParams = {
	getTitle?: (option: ISelectOption) => string;
	filter?: (option: ISelectOption) => boolean;
	emptyValueTitle?: string;
	addEmptyValue?: boolean;
};

export const getSelectOptions = (type: TNomenclature, params?: TParams) => {
	let result = [] as ISelectOption[];

	switch (type) {
		case 'AccountingUserTypeEnum':
			result = [
				{ title: 'Employé', value: AccountingUserTypeEnum.EMPLOYEE },
				{ title: 'Investisseur', value: AccountingUserTypeEnum.INVESTOR },
			] as ISelectOption[];
			break;

		case 'AccountingUserStatusEnum':
			result = [
				{ title: 'Actif', value: AccountingUserStatusEnum.ACTIVE },
				{ title: 'Désactivé', value: AccountingUserStatusEnum.DISABLED },
			] as ISelectOption[];
			break;

		case 'TransactionCategoryEnum':
			result = [
				{ title: 'Salaire', value: TransactionCategoryEnum.SALARY },
				{ title: 'Investisseur', value: TransactionCategoryEnum.INVESTOR },
				{ title: 'Achat in-app', value: TransactionCategoryEnum.IN_APP_PURCHASE },
				{ title: 'Marketing Heyama', value: TransactionCategoryEnum.MARKETING },
				{ title: 'Marketing Govoir', value: TransactionCategoryEnum.MARKETING_GOVOIR },
				{ title: 'Administration', value: TransactionCategoryEnum.ADMINISTRATION },
				{ title: 'Infrastructure', value: TransactionCategoryEnum.INFRASTRUCTURE },
				{ title: 'Banque', value: TransactionCategoryEnum.BANK_EXPENSE },
				{ title: 'Autre revenu', value: TransactionCategoryEnum.OTHER_INCOME },
				{ title: 'Autre', value: TransactionCategoryEnum.OTHER },
			] as ISelectOption[];
			break;

		case 'AdminUserPermissionsEnum':
			result = [
				{ title: 'Comptabilité', value: AdminUserPermissionsEnum.ACCOUNTING_MANAGER },
				{
					title: "Comptabilite - Vue d'ensemble",
					value: AdminUserPermissionsEnum.ACCOUNTING_OVERVIEW,
				},
				{ title: 'Vérification des bio', value: AdminUserPermissionsEnum.BIO_VERIF },
				{
					title: 'Certification des comptes',
					value: AdminUserPermissionsEnum.CERTIFICATION,
				},
				{ title: 'Utilisateurs', value: AdminUserPermissionsEnum.USERS },
				{ title: 'Historique', value: AdminUserPermissionsEnum.HISTORY },
				{ title: 'Promotions', value: AdminUserPermissionsEnum.PROMOTION },
				{ title: 'Vérification des photos', value: AdminUserPermissionsEnum.PHOTO_VERIF },
				{
					title: 'Certaines actions sur les profiles',
					value: AdminUserPermissionsEnum.PROFILE_DETAIL_ACTIONS,
				},
				{ title: 'Créators', value: AdminUserPermissionsEnum.REFERRAL },
				{
					title: 'Supprimer des créateurs',
					value: AdminUserPermissionsEnum.REFERRAL_DELETE,
				},
				{
					title: 'Messages Admins',
					value: AdminUserPermissionsEnum.SEND_ADMIN_MESSAGES,
				},
				{ title: 'Événements', value: AdminUserPermissionsEnum.EVENT },
				{ title: 'Marketing', value: AdminUserPermissionsEnum.MARKETING },
				{ title: 'Repports', value: AdminUserPermissionsEnum.REPORT },
				{ title: 'Paramètres', value: AdminUserPermissionsEnum.SETTINGS },
				{ title: 'Statistiques', value: AdminUserPermissionsEnum.STATS },
				{
					title: 'Ajouter ou retiter des pièces utilisateur',
					value: AdminUserPermissionsEnum.ADD_REMOVE_USER_COINS,
				},
				{
					title: 'Ajouter ou retirer un pack incognito utilisateur',
					value: AdminUserPermissionsEnum.ADD_REMOVE_USER_INCOGNITO,
				},
				{
					title: 'Ajouter un boost utilisateur',
					value: AdminUserPermissionsEnum.ADD_REMOVE_USER_BOOST,
				},
				{
					title: 'Gérer les vérificaions en tant que administrateur',
					value: AdminUserPermissionsEnum.HANDLE_ADMIN_REVIEW_REQUEST,
				},
				{
					title: 'Sous comptes utilisateurs',
					value: AdminUserPermissionsEnum.USERS_SUB_ACCOUNTS,
				},
				{
					title: 'Transactions utilisateurs',
					value: AdminUserPermissionsEnum.USERS_TRANSACTIONS,
				},
				{
					title: 'Paiements utilisateurs',
					value: AdminUserPermissionsEnum.USERS_PAYMENTS,
				},
				{
					title: 'Suspendre un utilisateur',
					value: AdminUserPermissionsEnum.SUSPEND_USER,
				},
				{
					title: 'Notifications push (campagnes)',
					value: AdminUserPermissionsEnum.BULK_NOTIFICATIONS,
				},
				{
					title: 'Forfaits de paiement',
					value: AdminUserPermissionsEnum.PAYMENT_PACKAGES,
				},
				{
					title: 'Fournisseurs de paiement',
					value: AdminUserPermissionsEnum.PAYMENT_PROVIDERS,
				},
				{
					title: 'Demandes de moyens de paiement',
					value: AdminUserPermissionsEnum.PAYMENT_METHOD_REQUESTS,
				},
			] as ISelectOption[];
			break;

		case 'VerificationStateEnum':
			result = [
				{
					title: 'Erreur de certification',
					value: VerificationStateEnum.CERTIFICATION_ERROR,
				},
				{ title: 'Blanc', value: VerificationStateEnum.WHITE_PERSON },
				{ title: 'Arnaque', value: VerificationStateEnum.SCAM },
				{ title: 'Autres', value: VerificationStateEnum.OTHERS },
			] as ISelectOption[];
			break;

		case 'CoinPackageNameEnum':
			result = [
				CoinPackageNameEnum.BRONZE,
				CoinPackageNameEnum.SILVER,
				CoinPackageNameEnum.GOLD,
				CoinPackageNameEnum.DIAMOND,
			].map(value => {
				const coins = PROMOTION_PRODUCT_PACKAGES.COINS?.find(
					pck => pck.name.toUpperCase() === value,
				)?.baseValue;
				return {
					title: coins ? `${value} (${coins.toLocaleString('fr-FR')} pièces)` : value,
					value,
				};
			}) as ISelectOption[];
			break;

		case 'IncognitoPackageNameEnum':
			result = [
				{ title: '1 mois', value: IncognitoPackageNameEnum.INCOGNITO_1_MONTH },
				{ title: '3 mois', value: IncognitoPackageNameEnum.INCOGNITO_3_MONTHS },
				{ title: '6 mois', value: IncognitoPackageNameEnum.INCOGNITO_6_MONTHS },
				{ title: '12 mois', value: IncognitoPackageNameEnum.INCOGNITO_12_MONTHS },
			] as ISelectOption[];
			break;

		case 'BoostPackageNameEnum':
			result = [
				{ title: '1 heure', value: BoostPackageNameEnum.BOOST_1_HOUR },
				{ title: '6 heures', value: BoostPackageNameEnum.BOOST_6_HOURS },
				{ title: '24 heures', value: BoostPackageNameEnum.BOOST_24_HOURS },
			] as ISelectOption[];
			break;

		case 'VerificationTypesEnum':
			result = [
				{ title: 'Certifications', value: VerificationTypesEnum.IDENTITY },
				{ title: 'Photos', value: VerificationTypesEnum.PROFILE_PHOTO },
				{ title: 'Bios', value: VerificationTypesEnum.BIO },
			] as ISelectOption[];
			break;

		case 'ReportReasonEnum':
			result = [
				{ title: 'Contenu explicite', value: ReportReasonEnum.EXPLICIT_CONTENT },
				{ title: 'Propos haineux', value: ReportReasonEnum.HATE_SPEECH },
				{ title: 'Faux profil', value: ReportReasonEnum.FAKE_PROFILE },
				{ title: 'Harcèlement', value: ReportReasonEnum.HARASSMENT },
				{ title: 'Arnaque', value: ReportReasonEnum.SCAM },
				{ title: 'Autre', value: ReportReasonEnum.OTHER },
				{ title: 'Remboursement', value: ReportReasonEnum.STORE_REFUND },
			] as ISelectOption[];
			break;

		case 'UserRewardSourceEnum':
			result = [
				{ title: 'STORE_REVIEW', value: UserRewardSourceEnum.STORE_REVIEW },
				{ title: 'INSTAGRAM', value: UserRewardSourceEnum.INSTAGRAM },
				{ title: 'FACEBOOK', value: UserRewardSourceEnum.FACEBOOK },
				{ title: 'TWITTER', value: UserRewardSourceEnum.TWITTER },
				{ title: 'TIKTOK', value: UserRewardSourceEnum.TIKTOK },
				{ title: 'GOVOIR_STORE_REVIEW', value: UserRewardSourceEnum.GOVOIR_STORE_REVIEW },
				{ title: 'GOVOIR_INSTAGRAM', value: UserRewardSourceEnum.GOVOIR_INSTAGRAM },
				{ title: 'GOVOIR_FACEBOOK', value: UserRewardSourceEnum.GOVOIR_FACEBOOK },
				{ title: 'GOVOIR_TIKTOK', value: UserRewardSourceEnum.GOVOIR_TIKTOK },
			] as ISelectOption[];
			break;

		case 'CoinAddedByEnum':
			result = [
				{ title: 'Enregistré via webhook', value: CoinAddedByEnum.PAYMENT_WEBHOOK },
				{ title: 'Enregistré manuellement', value: CoinAddedByEnum.MANUALLY },
			] as ISelectOption[];
			break;

		case 'ProductsEnum':
			result = [
				{
					title: 'Utilisateurs connectée uniquement',
					value: ProductsEnum.ONLINE_USERS_ONLY,
				},
				{ title: 'Filtre intension', value: ProductsEnum.INTENTION_FILTER },
				{ title: 'Activité de like', value: ProductsEnum.ACTIVITY_LIKE },
				{ title: 'Pièces achetées', value: ProductsEnum.PAID_COINS },
				{ title: 'Retour de like', value: ProductsEnum.REWIND_LIKE },
				{ title: 'Pièces gagnées', value: ProductsEnum.FREE_COINS },
				{ title: 'DM restreinte', value: ProductsEnum.RESTRICT_DM },
				{ title: 'Envoi DM', value: ProductsEnum.SEND_DM },
				{ title: 'Incognito acheté', value: ProductsEnum.PAID_INCOGNITO },
				{ title: 'Incognito offert', value: ProductsEnum.BONUS_INCOGNITO },
				{ title: 'Boost acheté', value: ProductsEnum.PAID_BOOST },
				{ title: 'Boost offert', value: ProductsEnum.BONUS_BOOST },
				{ title: 'Inscription événement', value: ProductsEnum.LIVE_EVENT_REGISTRATION },
				{ title: 'Remboursement événement', value: ProductsEnum.LIVE_EVENT_REFUND },
				{ title: 'Achat remboursé', value: ProductsEnum.STORE_REFUND },
			] as ISelectOption[];
			break;

		case 'UserAccountStatusEnum':
			result = [
				{ title: 'Actif', value: UserAccountStatusEnum.ACTIVE },
				{ title: 'Masqué', value: UserAccountStatusEnum.HIDDEN },
				{ title: 'En pause', value: UserAccountStatusEnum.PAUSED },
				{ title: 'Suspendu', value: UserAccountStatusEnum.SUSPENDED },
				{ title: 'Banni', value: UserAccountStatusEnum.BANNED },
				{ title: 'Supprimé', value: UserAccountStatusEnum.DELETED },
				{ title: 'Supprimé par un admin', value: UserAccountStatusEnum.ADMIN_DELETED },
			] as ISelectOption[];
			break;

		case 'DataTablePageSizeEnum':
			result = Object.keys(DataTablePageSizeEnum)
				.filter(l => !isNaN(Number(l)))
				.map(
					l =>
						({
							title:
								l === DataTablePageSizeEnum.all.toString()
									? 'Tous'
									: DataTablePageSizeEnum[l as any].replace('_', ''),
							value: l,
						}) as ISelectOption,
				);
			break;

		case 'TransactionTypeEnum':
			result = [
				{ title: 'Dépense', value: TransactionTypeEnum.EXPENSE },
				{ title: 'Revenu', value: TransactionTypeEnum.INCOME },
			] as ISelectOption[];
			break;

		case 'PaymentMethodEnum':
			result = [
				{ title: 'Transfert bancaire', value: PaymentMethodEnum.BANK_TRANSFERT },
				{ title: 'Mobile money', value: PaymentMethodEnum.MOBILE_MONEY },
				{ title: 'Paypal', value: PaymentMethodEnum.PAYPAL },
			] as ISelectOption[];
			break;

		case 'PaymentMethodRequestStatusEnum':
			result = [
				{ title: 'Nouveau', value: PaymentMethodRequestStatusEnum.NEW },
				{ title: 'Vu', value: PaymentMethodRequestStatusEnum.SEEN },
				{ title: 'Implémenté', value: PaymentMethodRequestStatusEnum.IMPLEMENTED },
			] as ISelectOption[];
			break;

		case 'CreatorTypeEnum':
			result = [
				{ title: 'Interne', value: CreatorTypeEnum.INTERN },
				{ title: 'Externe', value: CreatorTypeEnum.CONSULTANT },
			] as ISelectOption[];
			break;
		case 'ValueTypeEnum':
			result = [
				{ title: 'Pourcentage', value: ValueTypeEnum.PERCENTAGE },
				{ title: 'Valeur', value: ValueTypeEnum.VALUE },
			] as ISelectOption[];
			break;

		case 'CurrencyEnum':
			result = [
				{ title: 'Euro', value: CurrencyEnum.EUR },
				{ title: 'Dollar', value: CurrencyEnum.USD },
				{ title: 'Franc CFA', value: CurrencyEnum.CFA },
			] as ISelectOption[];
			break;

		case 'DmStateEnum':
			result = [
				{ title: 'En cours', value: DmStateEnum.PENDING },
				{ title: 'Accepté', value: DmStateEnum.ACCEPTED },
				{ title: 'Refusé', value: DmStateEnum.DENIED },
				{ title: 'Masqué', value: DmStateEnum.HIDDEN },
			] as ISelectOption[];
			break;

		case 'YesOrNoEnum':
			result = [
				{ title: 'Oui', value: YesOrNoEnum.YES },
				{ title: 'Non', value: YesOrNoEnum.NO },
			] as ISelectOption[];
			break;

		case 'CountryEnum':
			result = Object.keys(countries.getAlpha3Codes())
				.map(countryCodeIso3 => ({
					value: countryCodeIso3.toUpperCase() as CountryEnum,
					title: countries.getName(countryCodeIso3, 'fr')!,
				}))
				.sort((a, b) => (a.title > b.title ? 1 : -1)) as ISelectOption[];
			break;

		case 'GenderEnum':
			result = [
				{ title: 'Homme', value: GenderEnum.MALE },
				{ title: 'Femme', value: GenderEnum.FEMALE },
			] as ISelectOption[];
			break;

		case 'MonthEnum':
			result = [
				{
					title: 'Janvier',
					value: MonthEnum.JANUARY.toString(),
					data: MonthEnum.JANUARY,
				},
				{
					title: 'Février',
					value: MonthEnum.FEBRUARY.toString(),
					data: MonthEnum.FEBRUARY,
				},
				{ title: 'Mars', value: MonthEnum.MARCH.toString(), data: MonthEnum.MARCH },
				{ title: 'Avril', value: MonthEnum.APRIL.toString(), data: MonthEnum.APRIL },
				{ title: 'Mai', value: MonthEnum.MAY.toString(), data: MonthEnum.MAY },
				{ title: 'Juin', value: MonthEnum.JUNE.toString(), data: MonthEnum.JUNE },
				{ title: 'Juillet', value: MonthEnum.JULY.toString(), data: MonthEnum.JULY },
				{ title: 'Août', value: MonthEnum.AUGUST.toString(), data: MonthEnum.AUGUST },
				{
					title: 'Septembre',
					value: MonthEnum.SEPTEMBER.toString(),
					data: MonthEnum.SEPTEMBER,
				},
				{
					title: 'Octobre',
					value: MonthEnum.OCTOBER.toString(),
					data: MonthEnum.OCTOBER,
				},
				{
					title: 'Novembre',
					value: MonthEnum.NOVEMBER.toString(),
					data: MonthEnum.NOVEMBER,
				},
				{
					title: 'Décembre',
					value: MonthEnum.DECEMBER.toString(),
					data: MonthEnum.DECEMBER,
				},
			] as ISelectOption[];
			break;

		case 'PromotionStatusEnum':
			result = [
				{ title: 'En attente', value: PromotionStatusEnum.PENDING },
				{ title: 'En cours', value: PromotionStatusEnum.ACTIVE },
				{ title: 'Terminé', value: PromotionStatusEnum.FINISHED },
			] as ISelectOption[];
			break;

		default:
			break;
	}

	if (params?.addEmptyValue) {
		result = [{ title: params.emptyValueTitle ?? '', value: '' }, ...result];
	}

	if (params?.filter) {
		result = result.filter(params?.filter);
	}

	if (params?.getTitle) {
		result = [...result.map(l => ({ ...l, title: params.getTitle?.(l) as string }))];
	}

	return result;
};

